import { EyeIcon, PresentationFillIcon } from "@vericus/cadmus-icons";
import { Button, Text } from "@vericus/cadmus-ui";

import { AssessmentFragment, WorkSettingsFragment } from "@/generated/graphql";
import { useRootNavigate } from "@/routes";
import { formatDateFull, formatDateShort } from "@/utils/datetime";

import { useWorkDueDates } from "../hooks/use-work-due-dates";
import { ExtensionPill } from "./extension-pill";
import * as styles from "./home-page.css";
import { RailCardLayoutProps } from "./rail-card-layout";

interface InstructionProps extends RailCardLayoutProps {
  assessment: AssessmentFragment;
  groupWorkSettings?: WorkSettingsFragment | null;
  individualWorkSettings?: WorkSettingsFragment | null;
}

export function InstructionCard(props: InstructionProps) {
  const {
    assessment,
    groupWorkSettings,
    individualWorkSettings,
    layout = "wide",
    title,
  } = props;
  const rootNavigate = useRootNavigate();

  const {
    group: {
      dueDate: groupDueDate,
      originalDueDate: groupOriginalDue,
      isExtended: groupIsExtended,
    },
    individual: {
      dueDate: individualDueDate,
      originalDueDate: individualOriginalDue,
      isExtended: individualIsExtended,
    },
    peer: { dueDate: peerDueDate },
  } = useWorkDueDates(assessment, groupWorkSettings, individualWorkSettings);

  const card = (
    <div
      className={styles.stepCard}
      data-testid="instruction-card"
      data-layout={layout}
    >
      {layout === "stacked" ? (
        title != null &&
        title !== "" && (
          // A heading, not `div`, matching GroupCard's and GroupCharterCard's
          // identical title-kicker fix: same `RailCardLayoutProps` shape, so
          // this sits as a peer h2 section rather than nested under one.
          <Text kind="label" as="h2" color="indigo500">
            {title}
          </Text>
        )
      ) : (
        <>
          <Text kind="headingFour">Instructions</Text>
          <Text kind="bodyLine">
            Instructions for completing your report are available here.
          </Text>
        </>
      )}
      <div className={styles.instructionSheet[layout]}>
        <div
          className={
            layout === "stacked" ? styles.instructionRowContent : undefined
          }
        >
          <Text kind="headingFive" textTransform="capitalize">
            Group Work: {assessment.name}
          </Text>
          {groupDueDate !== null && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Text kind="bodySm">
                <strong>Due:</strong>{" "}
                {layout === "stacked"
                  ? formatDateShort(new Date(groupDueDate))
                  : formatDateFull(new Date(groupDueDate))}
              </Text>
              {groupIsExtended && groupOriginalDue !== null && (
                <ExtensionPill
                  originalDueDate={groupOriginalDue}
                  extendedDueDate={groupDueDate}
                />
              )}
            </div>
          )}
        </div>
        <Button
          kind="secondary"
          icon={<EyeIcon label="" size={24} />}
          onClick={() => rootNavigate("/instructions/group")}
        >
          View Instructions
        </Button>
      </div>
      {assessment.settings?.individualReportEnabled === true && (
        <div className={styles.instructionSheet[layout]}>
          <div
            className={
              layout === "stacked" ? styles.instructionRowContent : undefined
            }
          >
            <Text kind="headingFive">Individual Work</Text>
            {individualDueDate !== null && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Text kind="bodySm">
                  <strong>Due:</strong>{" "}
                  {layout === "stacked"
                    ? formatDateShort(new Date(individualDueDate))
                    : formatDateFull(new Date(individualDueDate))}
                </Text>
                {individualIsExtended && individualOriginalDue !== null && (
                  <ExtensionPill
                    originalDueDate={individualOriginalDue}
                    extendedDueDate={individualDueDate}
                  />
                )}
              </div>
            )}
          </div>
          <Button
            kind="secondary"
            icon={<EyeIcon label="" size={24} />}
            onClick={() => rootNavigate("/instructions/individual")}
          >
            View Instructions
          </Button>
        </div>
      )}
      {assessment.settings?.peerEvaluationEnabled === true && (
        <div className={styles.instructionSheet[layout]}>
          <div
            className={
              layout === "stacked" ? styles.instructionRowContent : undefined
            }
          >
            <Text kind="headingFive">Peer Evaluation</Text>
            {peerDueDate !== null && (
              <Text kind="bodySm">
                <strong>Due:</strong>{" "}
                {layout === "stacked"
                  ? formatDateShort(new Date(peerDueDate))
                  : formatDateFull(new Date(peerDueDate))}
              </Text>
            )}
          </div>
          <Button
            kind="secondary"
            icon={<EyeIcon label="" size={24} />}
            onClick={() => rootNavigate("/instructions/peer")}
          >
            View Instructions
          </Button>
        </div>
      )}
    </div>
  );

  // See group-card.tsx for why `stacked` drops the timeline icon entirely.
  if (layout === "stacked") {
    return card;
  }

  return (
    <div className={styles.steps}>
      <div className={styles.stepIcon["initial"]}>
        <PresentationFillIcon label="" size={30} />
      </div>
      {card}
    </div>
  );
}
